import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const POLL_MS = 15000;
const fmtTime = (iso) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export const ReferralThreadDialog = ({ referral, onClose, onPosted }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const rid = referral?.id;

  const load = useCallback(async () => {
    if (!rid) return;
    try { setMsgs((await api.get(`/partnerships/referrals/${rid}/messages`)).data || []); } catch (e) { /* silencieux */ }
  }, [rid]);

  useEffect(() => {
    if (!rid) return undefined;
    setMsgs([]); load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [rid, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/partnerships/referrals/${rid}/messages`, { text });
      setMsgs((m) => [...m, r.data]); setText(''); onPosted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Envoi impossible'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!referral} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="referral-thread-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Suivi : {referral?.client_name}</DialogTitle>
          <DialogDescription>{referral?.from_agency_name} → {referral?.to_agency_name}</DialogDescription>
        </DialogHeader>
        <div className="h-72 overflow-y-auto rounded-xl bg-[#F5EFE1]/60 p-3 space-y-2" data-testid="referral-thread-messages">
          {!msgs.length && <p className="text-sm text-gray-500 text-center pt-24">Aucun message. Lancez la discussion.</p>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`} data-testid={`thread-message-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.mine ? 'bg-[#2B3A2C] text-white rounded-br-sm' : 'bg-white text-[#2B3A2C] border border-[#B85C38]/15 rounded-bl-sm'}`}>
                <p className={`text-[10px] font-semibold mb-0.5 ${m.mine ? 'text-[#F5EFE1]/70' : 'text-[#B85C38]'}`}>
                  {m.author_name} · {m.agency_name}
                </p>
                <p className="whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] mt-1 ${m.mine ? 'text-white/50' : 'text-gray-400'}`}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2">
          <textarea
            rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Écrire un message… (Entrée pour envoyer)"
            className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-[#B85C38] outline-none"
            data-testid="referral-thread-input"
          />
          <Button onClick={send} disabled={busy || !text.trim()} className="self-end bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="referral-thread-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
